import {
  useCallback, useEffect, useRef, useState,
} from 'react';
import { useRelayEnvironment } from 'react-relay';

import useBatchedFetchErrorState from 'src/hooks/useBatchedFetchErrorState';
import {
  DATA_CONSUMED_KEY, DATA_GENERATED_KEY,
} from 'src/util/constants';
import logger from 'src/util/logger';
import {
  calculateTradeSetSummaryBatchSize,
  generateBatchTimeRanges,
} from 'src/util/tradeSetSummary/batchCalculator';

import {
  getTotalRulesCount,
} from '../helpers/dataBatching';
import {
  getPropertyDataFromCache,
  initialiseCache,
} from '../helpers/dataCaching';
import { PropertyShowLoadingActions } from '../reducer/constants';

/**
 * Custom hook that encapsulates all property data fetching.
 *
 * Handles:
 * - Batched data loading with progress tracking via reducer dispatch
 * - Data transformation from GraphQL response to meter objects
 * - Error handling and user feedback
 *
 * Caching is managed by the `dataCaching` module (module-level singleton).
 * This hook delegates to `getPropertyDataFromCache`, passing `propertyId`
 * so the cache layer can reset automatically on property changes.
 * See docs/design-docs/property-show-caching.md for full details.
 *
 * ## Cache bypass
 * To disable caching entirely, import and call `getPropertyDataInBatches`
 * directly (from `../helpers/dataBatching`) instead of going through
 * `getPropertyDataFromCache`. The cache layer internally calls
 * `getPropertyDataInBatches` on a miss, so bypassing the cache requires
 * no other code changes.
 *
 * Loading state is managed externally via the stateDispatchFunc reducer.
 * @param {object} params - Hook parameters
 * @param {string} params.propertyId - The property UUID to fetch data for
 * @param {object} params.dateSpan - Object with start and finish Luxon DateTime objects
 * @param {string} params.timezone - Timezone of the property
 * @param {object} params.initialData - Static/initial property data
 * @param {string} params.historianAggregation - Aggregation level
 * for historian data (e.g., 'PT30M', 'P1D')
 * @param {string} params.tradeAggregation - Aggregation level for trade data
 * @param {Function} [params.stateDispatchFunc] - Dispatch function for loading state reducer
 * @returns {object} Object containing:
 *   - combinedData: {object} Combined property data from GraphQL responses
 *   - cancelFunc: {Function} Cancels the in-flight fetch
 *   - clearError: {Function} Clears the error state without retrying
 *   - error: {Error|null} The last fetch error
 *   - handleRetry: {Function} Replays the failed query and resynchronises loading state
 *   - isRetrying: {boolean} True while a replay subscription is in flight
 */
const usePropertyData = ({
  propertyId,
  dateSpan,
  timezone,
  initialData,
  historianAggregation,
  tradeAggregation,
  stateDispatchFunc,
}) => {
  const environment = useRelayEnvironment();

  const [combinedData, setCombinedData] = useState(null);

  // Per-request cancellation token. Each new request creates a fresh { current: false }
  // object and stores it here. cancelFunc sets the active token's .current to true.
  // Old in-flight requests keep their own token reference, so they stay cancelled even
  // when a newer request starts — preventing the race where resetting a shared boolean
  // would "uncancel" a stale request.
  const cancelTokenRef = useRef({ current: false });

  const stateDispatchFuncRef = useRef(stateDispatchFunc);
  stateDispatchFuncRef.current = stateDispatchFunc;

  const onErrorDispatch = useCallback(() => {
    if (stateDispatchFuncRef.current) {
      stateDispatchFuncRef.current({ type: PropertyShowLoadingActions.CANCEL_REQUEST });
    }
  }, []);

  const {
    error,
    isRetrying,
    retryNonce,
    queryType,
    clearError,
    resolveRetry,
    handleFetchError,
    handleRetry: triggerRetry,
  } = useBatchedFetchErrorState({ onErrorDispatch });

  // Clear the module-level cache when the component unmounts so stale
  // data is not served when navigating back to the same property.
  useEffect(() => () => {
    initialiseCache(null);
  }, []);

  // Retry replays the entire pipeline by bumping retryNonce (via the shared hook)
  // and immediately clearing local data so the UI transitions out of the
  // stale-data view while the new fetch is in flight.
  const handleRetry = useCallback(() => {
    setCombinedData(null);
    triggerRetry();
  }, [triggerRetry]);

  // Single effect: fetch property data whenever the property, date span, or
  // time-series parameters change, and on explicit retry.
  useEffect(() => {
    if (!propertyId || !dateSpan) {
      logger.error('No property ID or date span found for property data fetching');
      return undefined;
    }

    logger.log('Fetching property data for property:', propertyId);

    // Create a fresh cancellation token for this request. Old in-flight requests
    // retain their own (cancelled) token, so they cannot be "uncancelled".
    const cancelToken = { current: false };
    cancelTokenRef.current = cancelToken;

    // Delegate to the read-through cache. On a hit, onDataFunc fires
    // immediately with cached data. On a miss, onBeforeFetchFunc fires
    // first, then the fetch runs and onDataFunc fires with fresh data.
    getPropertyDataFromCache({
      propertyId,
      dateSpan,
      aggregation: historianAggregation,
      fetchParams: {
        propertyId,
        timezone,
        environment,
        tradeAggregation,
        initialData,
      },
      cancelledRef: cancelToken,
      stateDispatchFunc,
      onBeforeFetchFunc: ({ segmentsExist }) => {
        if (cancelToken.current) return;
        if (stateDispatchFunc) {
          stateDispatchFunc({ type: PropertyShowLoadingActions.START_REQUEST });
          if (segmentsExist) {
            stateDispatchFunc({ type: PropertyShowLoadingActions.SET_PROPERTY_LOADED });
          }
        }
      },
      onDataFunc: (data, meters, rulesData, fromCache) => {
        if (cancelToken.current) return;
        setCombinedData(data);
        resolveRetry();
        if (stateDispatchFunc) {
          if (fromCache) {
            const totalRulesCount = getTotalRulesCount(rulesData);
            let batchCount = 0;
            // Only compute batch count when there are rules AND the cached
            // data contains actual meter data points. Without meter data the
            // trade summary fetch was skipped, so batchCount must stay 0.
            const cachedHasMeterData = data?.property?.meters?.edges?.some(
              (edge) => edge?.node?.[DATA_CONSUMED_KEY]?.data?.length > 0
                || edge?.node?.[DATA_GENERATED_KEY]?.data?.length > 0,
            );
            if (totalRulesCount > 0 && cachedHasMeterData) {
              const { batchSizeHours } = calculateTradeSetSummaryBatchSize({
                start: dateSpan.start,
                finish: dateSpan.finish,
                aggregation: historianAggregation,
                ruleCount: totalRulesCount,
              });
              batchCount = generateBatchTimeRanges({
                start: dateSpan.start,
                finish: dateSpan.finish,
                batchSizeHours,
              }).length;
            }
            stateDispatchFunc({
              type: PropertyShowLoadingActions.MARK_FULLY_LOADED,
              metersCount: meters.length,
              rulesCount: totalRulesCount,
              batchesLoaded: batchCount,
              batchesTotal: batchCount,
            });
          } else {
            stateDispatchFunc({ type: PropertyShowLoadingActions.MARK_FULLY_LOADED });
          }
        }
      },
      onErrorFunc: (fetchError) => {
        if (cancelToken.current) return;
        setCombinedData(null);
        handleFetchError(fetchError);
      },
    });

    return () => {
      cancelToken.current = true;
    };
  }, [
    handleFetchError,
    propertyId,
    retryNonce,
    stateDispatchFunc,
    dateSpan?.start,
    dateSpan?.finish,
    timezone,
    environment,
    historianAggregation,
    tradeAggregation,
    resolveRetry,
  ]);

  const cancelFunc = useCallback(() => {
    cancelTokenRef.current.current = true;
    logger.log('Data fetch cancelled by user');
  }, []);

  return {
    combinedData,
    cancelFunc,
    clearError,
    error,
    handleRetry,
    isRetrying,
    queryType,
  };
};

export default usePropertyData;
